import { useState } from 'react'

import { isBoolean } from 'lodash'
import type { OpenAPIV3_1 as OpenApi3 } from 'openapi-types'
import ReactMarkdown from 'react-markdown'

import { isNonNullable } from '@subaio/utils'

import { ButtonLink } from 'components/Buttons'
import { components } from 'components/DefaultMdxComponents'
import { Heading } from 'components/Heading'
import { useApiContext } from 'components/OpenApi/context'
import { Section } from 'components/Section'

import { Attribute } from '../../Attribute'
import { List, Wrapper } from '../../Common'
import { DeprecationContextProvider, useDeprecationContext } from '../../Deprecation'
import { Example } from '../../Example'
import { Payload } from '../Payload'
import { PropertyTitle } from '../PropertyTitle'
import { TitleWrapper } from '../TitleWrapper'

/** All parameter definitions on a given request/response includes where they belong. We group them based on that. */
const findAndMergeParameters = (
	source: OpenApi3.ParameterObject['in'] | OpenApi3.ApiKeySecurityScheme['in'],
	parameters: Array<OpenApi3.ParameterObject | OpenApi3.SchemaObject | OpenApi3.HeaderObject | undefined>,
	security: Array<
		OpenApi3.ParameterObject | OpenApi3.SchemaObject | OpenApi3.SecuritySchemeObject | OpenApi3.HeaderObject | undefined
	>,
) => [
	...parameters.filter((parameter) => parameter && 'in' in parameter && parameter.in === source).filter(isNonNullable),
	...security
		.filter((definition) => definition && 'in' in definition && definition.in === source)
		.filter(isNonNullable),
]

type Props = {
	linkPrefix: string
	description?: string

	definitions: Record<string, OpenApi3.SchemaObject | OpenApi3.ReferenceObject> | undefined
	parameters: Array<OpenApi3.ReferenceObject | OpenApi3.ParameterObject> | undefined
	requestBody: OpenApi3.RequestBodyObject | OpenApi3.ReferenceObject | undefined
	security: Array<Record<string, Array<unknown>>> | undefined
	securityDefinitions?: Record<string, OpenApi3.SecuritySchemeObject | OpenApi3.ReferenceObject>

	stickyOffset?: number
}

export const Request = ({
	linkPrefix,
	definitions,
	description,
	parameters,
	security,
	securityDefinitions,
	requestBody,
	stickyOffset,
}: Props) => {
	const { getRefValue } = useApiContext()
	/** All security definitions with their schema definition.
	 * Security values are linked weirdly with the object key instead of a `$ref` on the schema like everything else */
	const securityWithValues =
		security && security.length > 0
			? security
					.flatMap((value) => Object.keys(value))
					.map((key) => key)
					.map((key) => {
						const schema = securityDefinitions?.[key]
						if (schema && '$ref' in schema) return getRefValue(schema.$ref)
						return schema
					})
			: []
	/** All parameters for the request with full value from `components/schema` or `components/parameters`. */
	const parametersWithValues =
		parameters && parameters.length > 0
			? parameters.map(
					(parameter): OpenApi3.ParameterObject | OpenApi3.SchemaObject | OpenApi3.HeaderObject | undefined => {
						if ('$ref' in parameter) return getRefValue(parameter.$ref)
						return parameter
					},
				)
			: []

	const pathParameters = findAndMergeParameters('path', parametersWithValues, [])
	const queryParameters = findAndMergeParameters('query', parametersWithValues, securityWithValues)
	const headerParameters = findAndMergeParameters('header', parametersWithValues, securityWithValues)
	const cookieParameters = findAndMergeParameters('cookie', parametersWithValues, securityWithValues)

	const contentTypes = Object.keys(
		requestBody && 'content' in requestBody && requestBody.content ? requestBody.content : {},
	)
	const [selectedContentType, setSelectedContentType] = useState(contentTypes[0])
	const deprecationContext = useDeprecationContext()

	if (requestBody && '$ref' in requestBody) {
		console.error('Rendering of referenced request or response objects are not yet supported')
		return null
	}

	const selectedContent = requestBody ? requestBody.content[selectedContentType] : null

	return (
		<DeprecationContextProvider value={deprecationContext}>
			<Wrapper>
				<div>
					<Section
						title='Request'
						renderHeading={({ level, id, label, children }) => (
							<TitleWrapper>
								<Heading level={level} id={id} label={label}>
									{children}
								</Heading>
								{deprecationContext.hasDeprecated && (
									<ButtonLink
										label={
											deprecationContext.showDeprecated ? 'Hide deprecated properties' : 'Show deprecated properties'
										}
										slim
										onClick={deprecationContext.toggleShowDeprecated}
									/>
								)}
							</TitleWrapper>
						)}
						label='request'
						prefix={linkPrefix}
						level={3}
					>
						{description && <ReactMarkdown components={components}>{description}</ReactMarkdown>}
						{pathParameters.length > 0 && (
							<Section
								title='Path parameters'
								prefix={`${linkPrefix}-request`}
								level={4}
								renderHeading={(props) => <PropertyTitle {...props} />}
							>
								<List>
									{pathParameters.map((value, index) => (
										<Attribute
											key={index}
											name={'name' in value ? value.name : ''}
											value={
												'schema' in value && value.schema ? { ...value.schema, description: value.description } : value
											}
											isRequired={'required' in value && isBoolean(value.required) ? value.required : null}
											linkPrefix={`${linkPrefix}-request-path-parameters`}
										/>
									))}
								</List>
							</Section>
						)}
						{queryParameters.length > 0 && (
							<Section
								title='Query parameters'
								prefix={`${linkPrefix}-request`}
								level={4}
								renderHeading={(props) => <PropertyTitle {...props} />}
							>
								<List>
									{queryParameters.map((value, index) => (
										<Attribute
											key={index}
											name={'name' in value ? value.name : ''}
											value={
												'schema' in value && value.schema ? { ...value.schema, description: value.description } : value
											}
											isRequired={'required' in value && isBoolean(value.required) ? value.required : null}
											linkPrefix={`${linkPrefix}-request-query-parameters`}
										/>
									))}
								</List>
							</Section>
						)}
						{headerParameters.length > 0 && (
							<Section
								title='Headers'
								prefix={`${linkPrefix}-request`}
								level={4}
								renderHeading={(props) => <PropertyTitle {...props} />}
							>
								<List>
									{headerParameters.map((value, index) => (
										<Attribute
											key={index}
											name={'name' in value ? value.name : ''}
											value={
												'schema' in value && value.schema
													? {
															...value.schema,
															description: value.description,
															example: value.example,
														}
													: value
											}
											isRequired={'required' in value && isBoolean(value.required) ? value.required : null}
											linkPrefix={`${linkPrefix}-request-headers`}
										/>
									))}
									{securityWithValues.length > 0 &&
										securityWithValues.map((security, index) => {
											if (security && 'type' in security && security.type === 'http') {
												return (
													<Attribute
														key={index}
														name='Authorization'
														value={security}
														linkPrefix={`${linkPrefix}-request-headers`}
														isRequired
													/>
												)
											}
											return null
										})}
								</List>
							</Section>
						)}
						{cookieParameters.length > 0 && (
							<Section
								title='Cookies'
								prefix={`${linkPrefix}-request`}
								level={4}
								renderHeading={(props) => <PropertyTitle {...props} />}
							>
								<List>
									{cookieParameters.map((value, index) => (
										<Attribute
											key={index}
											name={'name' in value ? value.name : ''}
											value={
												'schema' in value && value.schema ? { ...value.schema, description: value.description } : value
											}
											isRequired={'required' in value && isBoolean(value.required) ? value.required : null}
											linkPrefix={`${linkPrefix}-request-cookies`}
										/>
									))}
								</List>
							</Section>
						)}
						{selectedContent && (
							<Section
								title='Payload'
								prefix={`${linkPrefix}-request`}
								level={4}
								renderHeading={(props) => <PropertyTitle {...props} />}
							>
								<Payload data={selectedContent} linkPrefix={`${linkPrefix}-request-payload`} />
							</Section>
						)}
					</Section>
				</div>
				<Example
					stickyOffset={stickyOffset}
					title='Request body'
					data={requestBody}
					definitions={definitions}
					selectedContentType={selectedContentType}
					setSelectedContentType={setSelectedContentType}
				/>
			</Wrapper>
		</DeprecationContextProvider>
	)
}
